import { Suspense } from "react";
import { useAtomValue } from "jotai";
import { Icon, Text } from "@zapier/design-system";
import { Tooltip } from "@zapier/design-system-beta";
import { formatTimeUnit, Time } from "@zapier/date-canvas";
import {
  hasZslAtom,
  latestTimeSavedAtomFamily,
  showTimeSpentOrSavedAtom,
  zapUsageAsyncAtomFamily,
} from "@zapier/canvas-state";

import styles from "./TimeMetrics.module.css";

const size = 16;

type TimeMetricProps = {
  tooltipContent: string;
  textValue: string;
};

function TimeMetric({ tooltipContent, textValue }: TimeMetricProps) {
  return (
    <Tooltip placement="top" content={tooltipContent}>
      <Tooltip.FocusableTrigger>
        <div className={styles.contentWrapper}>
          <Icon name="miscClock" size={size} color="TextWeaker" />
          <Text type="MinimalPrint2" color="TextWeaker">
            {textValue}
          </Text>
        </div>
      </Tooltip.FocusableTrigger>
    </Tooltip>
  );
}

function SuspendedTimeSavedContent({ zapId }: { zapId: string }) {
  const timeSaved = useAtomValue(latestTimeSavedAtomFamily(zapId));
  const usage = useAtomValue(zapUsageAsyncAtomFamily(zapId));

  if (!timeSaved || !Number(timeSaved.value) || usage === null) return null;

  const { unit, value } = formatTimeUnit(timeSaved);

  const totalTimeSaved = (Number(timeSaved?.value) || 0) * usage.success;

  const convertedTotalTimeSaved = formatTimeUnit({
    value: String(totalTimeSaved),
    unit: timeSaved.unit,
  });

  const tooltipContent = totalTimeSaved
    ? `${Math.round(convertedTotalTimeSaved.value).toLocaleString()}${convertedTotalTimeSaved.unit[0]} saved, last 30 days`
    : "";

  return (
    <TimeMetric
      tooltipContent={tooltipContent}
      textValue={`${value}${unit[0]} saved`}
    />
  );
}

// For zap nodes
export function TimeSaved({ zapId }: { zapId: string | undefined }) {
  const hasZsl = useAtomValue(hasZslAtom);
  const showTimeSaved = useAtomValue(showTimeSpentOrSavedAtom);
  if (!showTimeSaved || !zapId || hasZsl) return null;

  return (
    <Suspense fallback={null}>
      <SuspendedTimeSavedContent zapId={zapId} />
    </Suspense>
  );
}

// For default nodes
export function TimeSpent({ timeSpent }: { timeSpent: Time | undefined }) {
  const showTimeSpent = useAtomValue(showTimeSpentOrSavedAtom);

  if (!showTimeSpent || !timeSpent || !Number(timeSpent.value)) return null;

  const { unit, value } = formatTimeUnit(timeSpent);

  return (
    <TimeMetric
      tooltipContent="Time spent"
      textValue={`${value}${unit[0]} spent`}
    />
  );
}
